import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import {AddOutlined} from '@material-ui/icons';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import Message from '../../../shared/message/Message.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {
    descricaoTipoProcessoVestibular,
    paraProcessoVestibularDoProcesso,
    validarProcessoVestibular
} from '../../components/processo/processoSelecao.js';

const FILTER_OPTIONS = {filterByColumn: true};
const COMPARATOR_KEYS = ['id'];
const TABLE_CONTAINER_PROPS = {component: 'div'};
const COLUNAS = [
    {label: 'Código', field: 'id', filterable: true, valueGetter: valor => ouTraco(valor)},
    {label: 'Descrição', field: 'descricao', filterable: true, valueGetter: valor => ouTraco(valor)},
    {label: 'Tipo', field: 'tipo', filterable: true, valueGetter: valor => ouTraco(descricaoTipoProcessoVestibular(valor))}
];

const useStyles = makeStyles(theme => ({
    paper: {
        width: 1000,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    orientacao: {
        marginBottom: theme.spacing(2)
    },
    mensagem: {
        marginBottom: theme.spacing(2)
    }
}));

/*
 * Processo seletivo (vestibular) permitido no processo de seleção
 * (_formSelecionarProcessoSeletivo do PrimeFaces): lista com filtro por
 * Código, Descrição e Tipo e seleção única. Sem seleção, repetido ou além do
 * único permitido ao Colégio Técnico, a mensagem aparece aqui e nada é adicionado.
 */
export default function DialogProcessoVestibular({open, onClose, processos = [], processosVestibular = [], idPrograma, onAdicionar}) {
    const classes = useStyles();
    const [selecionado, setSelecionado] = useState(null);
    const [mensagem, setMensagem] = useState('');

    const aoAbrir = () => {
        setSelecionado(null);
        setMensagem('');
    };

    const selecionar = processo => {
        setSelecionado(processo);
        setMensagem('');
    };

    const adicionar = () => {
        const erro = validarProcessoVestibular(processosVestibular, selecionado, idPrograma);
        if (erro) {
            setMensagem(erro);
            return;
        }
        onAdicionar(paraProcessoVestibularDoProcesso(selecionado));
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering: aoAbrir}} scroll='body'
                aria-labelledby='dialog-processo-vestibular-titulo' classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-processo-vestibular-titulo'>Selecionar Processo Seletivo</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                <Typography variant='body2' className={classes.orientacao}>
                    Selecione o processo seletivo desejado.
                </Typography>
                {!!mensagem && (
                    <div className={classes.mensagem}>
                        <Message type='error' variant='standard'>{mensagem}</Message>
                    </div>
                )}
                <DataGrid
                    rows={processos}
                    columns={COLUNAS}
                    comparatorKeys={COMPARATOR_KEYS}
                    filterOptions={FILTER_OPTIONS}
                    paginate
                    paginateOptions={PAGINACAO_PADRAO}
                    select
                    selectOptions={{
                        mode: 'single',
                        selectedRows: selecionado ? [selecionado] : [],
                        onSelect: processo => selecionar(processo)
                    }}
                    tableContainerProps={TABLE_CONTAINER_PROPS}
                    noRecordsText='Nenhum processo seletivo encontrado.'/>
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose}>Fechar</TextButton>
                <ContainedButton startIcon={<AddOutlined/>} onClick={adicionar}>Adicionar</ContainedButton>
            </DialogActions>
        </Dialog>
    );
}

DialogProcessoVestibular.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Processos vestibulares do apoio: [{id, descricao, tipo}]. */
    processos: PropTypes.array,
    /* Processos já permitidos no processo de seleção. */
    processosVestibular: PropTypes.array,
    idPrograma: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /* Recebe {id: null, idProcessoVestibular, descricao, tipo}. */
    onAdicionar: PropTypes.func.isRequired
};
